/**
 * Custom properties laid over a theme on the root element — the one place that writes them.
 *
 * The trainer's brand colour re-derives the accent family, and the derivation differs per theme
 * (an ink darkens on Flame and lightens on Flame Dark), so an override is a PAIR: one set of values
 * for each theme class. This module holds the current pair and paints the half that matches the
 * class on `<html>`. It knows nothing about brands: `entities/brand-color` computes the pair, the
 * signed-in shell hands it here, and nothing else writes inline custom properties on the root.
 *
 * ── Why the class and the overrides are painted from ONE call ────────────────────────────────
 * `startThemeSync` calls `paintThemeOverrides` right after it swaps the theme class, inside the same
 * synchronous store listener. A theme toggle therefore swaps the class and the brand's values for
 * the new theme before the browser can paint a frame between them. A React effect that re-applied
 * them after the re-render would be a frame late whenever the render is.
 *
 * ── What the pre-paint script left behind is removed like anything else ──────────────────────
 * `public/theme-init.js` may already have written the remembered brand's values on `<html>` before
 * any module ran. A paint removes every inline CUSTOM property the element carries that the
 * current set does not name, whoever wrote it, so a `setThemeOverrides(null)` takes those off too.
 * That is safe because this module and that script are the only writers of inline custom
 * properties on the root. Their VALUES are not adopted: until the shell hands over a pair, a theme
 * flip clears them, which is invisible because nothing but the colour-agnostic session loader
 * stands on screen then.
 */

import { THEME_CLASS } from './themeContract';

/** One override set per theme class. Names are custom properties (`--accent`), values are CSS. */
export interface ThemeOverrides {
  readonly light: Readonly<Record<string, string>>;
  readonly dark: Readonly<Record<string, string>>;
}

let current: ThemeOverrides | null = null;

function inlineCustomProperties(root: HTMLElement): string[] {
  const names: string[] = [];
  for (let index = 0; index < root.style.length; index += 1) {
    const name = root.style.item(index);
    if (name.startsWith('--')) names.push(name);
  }
  return names;
}

/** Paint the half of the current pair that matches the theme class on `root`. */
export function paintThemeOverrides(root: HTMLElement): void {
  const isDark = root.classList.contains(THEME_CLASS.dark);
  const values = current === null ? {} : isDark ? current.dark : current.light;

  for (const name of inlineCustomProperties(root)) {
    if (!(name in values)) root.style.removeProperty(name);
  }
  for (const [name, value] of Object.entries(values)) {
    root.style.setProperty(name, value);
  }
}

/** Replace the pair and paint it now. `null` is the shipped theme, with nothing laid over it. */
export function setThemeOverrides(next: ThemeOverrides | null): void {
  current = next;
  paintThemeOverrides(document.documentElement);
}
